import React from 'react';
import { ArrowLeft, FileText, Mail, Globe } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const TermsAndConditions = () => {
  const navigate = useNavigate();
  const lastUpdated = "April 23, 2026";
  const companyName = "CREATRVERSE";
  const companyEmail = "darren@creatrverse.com";
  const websiteUrl = "https://operatr.me";

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-black border-b border-white/20">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              data-testid="back-button"
            >
              <ArrowLeft size={20} />
            </button>
            <div className="flex items-center gap-3">
              <FileText className="text-purple-500" size={24} />
              <h1 className="text-xl font-bold">Terms and Conditions</h1>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="prose prose-invert max-w-none">
          <p className="text-gray-400 mb-8">Last Updated: {lastUpdated}</p>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4 text-white">1. Agreement to Terms</h2>
            <p className="text-gray-300 leading-relaxed">
              Welcome to {companyName}. These Terms and Conditions ("Terms") govern your access to and use of our 
              website, mobile application, and services (collectively, the "Services"). By accessing or using our 
              Services, you agree to be bound by these Terms.
            </p>
            <p className="text-gray-300 leading-relaxed mt-4">
              If you do not agree to these Terms, you must not access or use our Services. We reserve the right to 
              modify these Terms at any time, and such modifications will be effective immediately upon posting.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4 text-white">2. Eligibility</h2>
            <p className="text-gray-300 leading-relaxed">
              You must be at least 18 years old to use our Services. By using our Services, you represent and warrant 
              that you are at least 18 years old and have the legal capacity to enter into these Terms. If you are 
              using our Services on behalf of a business or organization, you represent that you have the authority 
              to bind that entity to these Terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4 text-white">3. Account Registration</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              To access certain features of our Services, you must create an account. When creating an account, you agree to:
            </p>
            <ul className="list-disc pl-6 text-gray-300 space-y-2">
              <li>Provide accurate, current, and complete information</li>
              <li>Maintain and update your information to keep it accurate</li>
              <li>Keep your password secure and confidential</li>
              <li>Notify us immediately of any unauthorized access to your account</li>
              <li>Be responsible for all activities that occur under your account</li>
            </ul>
            <p className="text-gray-300 leading-relaxed mt-4">
              We reserve the right to suspend or terminate your account if any information provided is found to be 
              inaccurate, false, or in violation of these Terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4 text-white">4. Services Description</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              {companyName} provides a comprehensive business dashboard platform that includes:
            </p>
            <ul className="list-disc pl-6 text-gray-300 space-y-2">
              <li><strong>CRM & Lead Management:</strong> Tools for managing customer relationships and leads</li>
              <li><strong>Communications:</strong> Email integration and management services</li>
              <li><strong>Task Management:</strong> Task tracking and calendar functionality</li>
              <li><strong>Media Portfolio:</strong> Audio and media management, including beat store functionality</li>
              <li><strong>AI Assistant:</strong> AI-powered tools for productivity enhancement</li>
              <li><strong>Team Collaboration:</strong> Multi-user team management features</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4 text-white">5. Subscription and Payment</h2>
            
            <h3 className="text-xl font-semibold mb-3 text-white">5.1 Subscription Plans</h3>
            <p className="text-gray-300 leading-relaxed mb-4">
              We offer various subscription plans with different features and pricing. Details of each plan are 
              available on our pricing page. We reserve the right to modify pricing and features at any time.
            </p>

            <h3 className="text-xl font-semibold mb-3 text-white">5.2 Payment Terms</h3>
            <ul className="list-disc pl-6 text-gray-300 space-y-2">
              <li>All payments are processed securely through our payment processor (Stripe)</li>
              <li>Subscription fees are billed in advance on a monthly or annual basis</li>
              <li>All fees are non-refundable unless otherwise stated</li>
              <li>You are responsible for all applicable taxes</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3 mt-6 text-white">5.3 Cancellation</h3>
            <p className="text-gray-300 leading-relaxed">
              You may cancel your subscription at any time. Upon cancellation, you will continue to have access to 
              your subscription until the end of your current billing period. No refunds will be provided for partial 
              subscription periods.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4 text-white">6. User Content</h2>
            
            <h3 className="text-xl font-semibold mb-3 text-white">6.1 Your Content</h3>
            <p className="text-gray-300 leading-relaxed mb-4">
              You retain ownership of all content you upload, submit, or create through our Services ("User Content"). 
              By uploading User Content, you grant us a non-exclusive, worldwide, royalty-free license to use, store, 
              and process your content solely for the purpose of providing our Services.
            </p>

            <h3 className="text-xl font-semibold mb-3 text-white">6.2 Content Restrictions</h3>
            <p className="text-gray-300 leading-relaxed mb-4">
              You agree not to upload, post, or transmit any content that:
            </p>
            <ul className="list-disc pl-6 text-gray-300 space-y-2">
              <li>Infringes on intellectual property rights of others</li>
              <li>Is unlawful, defamatory, obscene, or offensive</li>
              <li>Contains viruses, malware, or harmful code</li>
              <li>Violates the privacy or publicity rights of others</li>
              <li>Promotes illegal activities or violence</li>
              <li>Constitutes spam or unsolicited commercial communications</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4 text-white">7. Beat Store Terms</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              If you use our Beat Store functionality to sell or purchase audio content:
            </p>
            <ul className="list-disc pl-6 text-gray-300 space-y-2">
              <li><strong>Sellers:</strong> You represent that you own or have the necessary rights to sell the audio content</li>
              <li><strong>Buyers:</strong> Purchases grant you a license to use the content as specified at the time of purchase</li>
              <li><strong>Fees:</strong> We may charge transaction fees for sales made through the platform</li>
              <li><strong>Disputes:</strong> We are not responsible for disputes between buyers and sellers</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4 text-white">8. Third-Party Services</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              Our Services integrate with third-party services including but not limited to:
            </p>
            <ul className="list-disc pl-6 text-gray-300 space-y-2">
              <li>Google (authentication, Gmail, Calendar)</li>
              <li>Stripe (payment processing)</li>
              <li>Twilio (SMS and communication)</li>
              <li>Nylas (email synchronization)</li>
              <li>OpenAI (AI features)</li>
            </ul>
            <p className="text-gray-300 leading-relaxed mt-4">
              Your use of these third-party services is subject to their respective terms of service and privacy 
              policies. We are not responsible for the actions or policies of third-party service providers.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4 text-white">9. Intellectual Property</h2>
            <p className="text-gray-300 leading-relaxed">
              The Services and all content, features, and functionality (excluding User Content) are owned by 
              {companyName} and are protected by copyright, trademark, and other intellectual property laws. 
              You may not copy, modify, distribute, sell, or lease any part of our Services without our prior 
              written consent.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4 text-white">10. Prohibited Activities</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              You agree not to:
            </p>
            <ul className="list-disc pl-6 text-gray-300 space-y-2">
              <li>Use our Services for any illegal purpose</li>
              <li>Attempt to gain unauthorized access to our systems or other users' accounts</li>
              <li>Interfere with or disrupt the integrity of our Services</li>
              <li>Use automated means to access our Services without permission</li>
              <li>Impersonate another person or entity</li>
              <li>Harass, abuse, or harm other users</li>
              <li>Reverse engineer or attempt to extract source code from our Services</li>
              <li>Use our Services to send unsolicited communications</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4 text-white">11. Disclaimer of Warranties</h2>
            <p className="text-gray-300 leading-relaxed">
              OUR SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS 
              OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR 
              PURPOSE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT OUR SERVICES WILL BE UNINTERRUPTED, ERROR-FREE, 
              OR SECURE.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4 text-white">12. Limitation of Liability</h2>
            <p className="text-gray-300 leading-relaxed">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, {companyName.toUpperCase()} SHALL NOT BE LIABLE FOR ANY INDIRECT, 
              INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, 
              DATA, OR GOODWILL, ARISING OUT OF OR RELATED TO YOUR USE OF OUR SERVICES. OUR TOTAL LIABILITY SHALL NOT 
              EXCEED THE AMOUNT YOU PAID TO US IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4 text-white">13. Indemnification</h2>
            <p className="text-gray-300 leading-relaxed">
              You agree to indemnify, defend, and hold harmless {companyName}, its officers, directors, employees, 
              and agents from and against any claims, liabilities, damages, losses, and expenses arising out of or 
              related to your use of our Services, your User Content, or your violation of these Terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4 text-white">14. Termination</h2>
            <p className="text-gray-300 leading-relaxed">
              We may terminate or suspend your access to our Services at any time, with or without cause, and with 
              or without notice. Upon termination, your right to use our Services will immediately cease. All 
              provisions of these Terms which by their nature should survive termination shall survive, including 
              ownership provisions, warranty disclaimers, and limitations of liability.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4 text-white">15. Governing Law</h2>
            <p className="text-gray-300 leading-relaxed">
              These Terms shall be governed by and construed in accordance with the laws of the United States, 
              without regard to its conflict of law provisions. Any disputes arising from these Terms shall be 
              resolved in the courts located in the United States.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4 text-white">16. Severability</h2>
            <p className="text-gray-300 leading-relaxed">
              If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions 
              shall continue in full force and effect.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4 text-white">17. Entire Agreement</h2>
            <p className="text-gray-300 leading-relaxed">
              These Terms, together with our Privacy Policy and any other legal notices published on our Services, 
              constitute the entire agreement between you and {companyName} regarding your use of our Services.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4 text-white">18. Contact Us</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              If you have any questions about these Terms, please contact us:
            </p>
            <div className="bg-white/5 border border-white/20 rounded-xl p-6 space-y-3">
              <div className="flex items-center gap-3">
                <Globe size={18} className="text-purple-400" />
                <span className="text-gray-300">{companyName}</span>
              </div>
              <div className="flex items-center gap-3">
                <Mail size={18} className="text-purple-400" />
                <a href={`mailto:${companyEmail}`} className="text-purple-400 hover:text-purple-300">{companyEmail}</a>
              </div>
              <div className="flex items-center gap-3">
                <Globe size={18} className="text-purple-400" />
                <a href={websiteUrl} className="text-purple-400 hover:text-purple-300">{websiteUrl}</a>
              </div>
            </div>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/20 py-6">
        <div className="max-w-4xl mx-auto px-4 text-center text-gray-500 text-sm">
          © {new Date().getFullYear()} {companyName}. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default TermsAndConditions;
